import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService, HttpMethod } from '../api.service';
import { Hedge } from '../pricing/hedge.model';
import { SasTokenModel } from '../../shared-api/images/sas-token.model';
import { PagingRequest } from 'src/app/shared-api/models/paging.model';
import { HedgeVendorListItemModel } from './models/hedge-vendors-response.model';
import { PagingResponse } from '../vendor/models/vendor-for-new-po.model';
import { LinkedPOListItem } from './models/linked-po-list-item.model';
import { HedgeForVendorListItemModel } from './models/hedge-for-vendor-list-item.model';

@Injectable({
    providedIn: 'root'
})
export class HedgeApiService extends ApiService
{
    constructor(protected http: HttpClient)
    {
        super(http);
    }

    getAllHedges(locationId: number, includeFilled: boolean): Observable<Hedge[]>
    {
        return this.apiRequest<any[]>(this.getAllHedgesUrl(locationId, includeFilled), HttpMethod.Get)
            .pipe(map(json => json.map(item => new Hedge(item))));
    }

    getHedgesForVendor(vendorId: number, includeFilled = false): Observable<Hedge[]>
    {
        return this.apiRequest<any[]>(this.getHedgesForVendorUrl(vendorId, includeFilled), HttpMethod.Get)
            .pipe(map(hedges => hedges.map(item => new Hedge(item))));
    }

    addHedge(hedge: Hedge): Observable<Hedge>
    {
        return this.apiRequest(this.addHedgeUrl, HttpMethod.Post, JSON.stringify(hedge))
            .pipe(map(json => new Hedge(json)));
    }

    updateHedge(hedge: Hedge): Observable<Hedge>
    {
        return this.apiRequest(this.updateHedgeUrl, HttpMethod.Put, JSON.stringify(hedge))
            .pipe(map(json => new Hedge(json)));
    }

    deleteHedge(hedgeId: number): Observable<null>
    {
        return this.apiRequest(this.deleteHedgeUrl(hedgeId), HttpMethod.Delete);
    }

    getSasTokenUrl(filename: string): Observable<SasTokenModel>
    {
        return this.apiRequest<SasTokenModel>(this.getSasTokenUrlUrl(filename), HttpMethod.Get)
            .pipe(map(response => new SasTokenModel(response)));
    }

    setLsrSignature(id: number, url: string): Observable<boolean>
    {
        const body = { id, url };
        return this.apiRequest<any>(this.setLsrSignatureUrl, HttpMethod.Put, JSON.stringify(body)).pipe(map(() => true));
    }

    setSignaturePage(id: number, url: string): Observable<boolean>
    {
        const body = { id, url };
        return this.apiRequest<any>(this.setSignaturePageUrl, HttpMethod.Put, JSON.stringify(body)).pipe(map(() => true));
    }

    setReceivingLog(id: number, url: string): Observable<boolean>
    {
        const body = { id, url };
        return this.apiRequest<any>(this.setReceivingLogUrl, HttpMethod.Put, JSON.stringify(body)).pipe(map(() => true));
    }

    setFinalSettlementLog(id: number, url: string): Observable<boolean>
    {
        const body = { id, url };
        return this.apiRequest<any>(this.setFinalSettlementLogUrl, HttpMethod.Put, JSON.stringify(body)).pipe(map(() => true));
    }

    getVendors(locationId: number, options?: PagingRequest): Observable<PagingResponse<HedgeVendorListItemModel>> {
        return this.apiRequest<PagingResponse<HedgeVendorListItemModel>>(this.getVendorsUrl(locationId, options), HttpMethod.Get);
    }
    getLinkedPOs(hedgeId: number, options?: PagingRequest): Observable<PagingResponse<LinkedPOListItem>> {
        return this.apiRequest<PagingResponse<LinkedPOListItem>>(this.getLinkedPOsUrl(hedgeId, options), HttpMethod.Get);
    }

    getHedgesForVendorV2(vendorId: number, options?: PagingRequest): Observable<PagingResponse<HedgeForVendorListItemModel>> {
        return this.apiRequest<PagingResponse<HedgeForVendorListItemModel>>(
            this.getHedgesForVendorV2Url(vendorId, options), HttpMethod.Get
        );
    }

    getAllHedgesV2(locationId: number, options?: PagingRequest): Observable<PagingResponse<Hedge>> {
        return this.apiRequest<PagingResponse<any>>(this.getAllHedgesV2Url(locationId, options), HttpMethod.Get).pipe(
          map(res => ({
            ...res,
            items: res.items.map((x: any) => new Hedge(x)),
          }))
        );
}
    // =============================================================================================================================
    // URLs
    // =============================================================================================================================
    /* tslint:disable:member-ordering */
    // tslint:disable:max-line-length
    private get baseUrl(): string { return this.baseApiUrl + 'hedges'; }
    private getHedgesForVendorUrl(vendorId: number, includeFilled: boolean): string { return `${this.baseUrl}/hedgesForVendor?vendorId=${vendorId == null ? '' : vendorId}&includeFilled=${includeFilled}`; }
    private getAllHedgesUrl(locationId: number, includeFilled: boolean): string { return `${this.baseUrl}?locationId=${locationId}&includeFilled=${includeFilled}`; }
    private get addHedgeUrl(): string { return `${this.baseUrl}`; }
    private get updateHedgeUrl(): string { return `${this.baseUrl}`; }
    private deleteHedgeUrl(id: number): string { return `${this.baseUrl}/${id}`; }
    private getSasTokenUrlUrl(filename: string): string { return `${this.baseUrl}/sas/${filename}`; }
    private get setLsrSignatureUrl(): string { return `${this.baseUrl}/setLsrSignature`; }
    private get setSignaturePageUrl(): string { return `${this.baseUrl}/setSignaturePage`; }
    private get setReceivingLogUrl(): string { return `${this.baseUrl}/setReceivingLog`; }
    private get setFinalSettlementLogUrl(): string { return `${this.baseUrl}/setFinalSettlementLog`; }

    private baseApiV2 = this.baseApiUrl + 'v2/admin'
    private getVendorsUrl(locationId: number, options?: PagingRequest): string { 
        let url = `${this.baseApiV2}/hedges/vendors?locationId=${locationId}`; 
        if(!options)
            return url;

        url = url + '&' + this.buildPagingRequest(options); 
        return url;
    }
    private getHedgesForVendorV2Url(vendorId: number, options?: PagingRequest): string {
        const url = `${this.baseUrl}/vendor/${vendorId}`;
        if (!options) return url;
        return url + '?' + this.buildPagingRequest(options);
    }
    private getLinkedPOsUrl(hedgeId: number, options?: PagingRequest): string {
        const url = `${this.baseUrl}/${hedgeId}/purchaseOrders`;
        if (!options) return url;
        return url + '?' + this.buildPagingRequest(options);
    }
    private getAllHedgesV2Url(locationId: number, options?: PagingRequest): string {
        let url = `${this.baseApiV2}/hedges?locationId=${locationId}&showRequested=false`; 
        if(!options)
            return url;

        url = url + '&' + this.buildPagingRequest(options); 
        return url; 
    }
}
